'use strict';

function _asyncToGenerator(fn) { return function () { var gen = fn.apply(this, arguments); return new Promise(function (resolve, reject) { function step(key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { return Promise.resolve(value).then(function (value) { step("next", value); }, function (err) { step("throw", err); }); } } return step("next"); }); }; }

// const EthQuery = require('ethjs-query')
const EthQuery = require('eth-query');
const EventEmitter = require('events');
const pify = require('pify');
const hexUtils = require('./hexUtils');
const incrementHexNumber = hexUtils.incrementHexNumber;
const sec = 1000;
const min = 60 * sec;

class RpcBlockTracker extends EventEmitter {

  constructor(opts = {}) {
    super();
    if (!opts.provider) throw new Error('RpcBlockTracker - no provider specified.');
    this._provider = opts.provider;
    this._query = new EthQuery(opts.provider);
    // config
    this._pollingInterval = opts.pollingInterval || 4 * sec;
    this._syncingTimeout = opts.syncingTimeout || 1 * min;
    // state
    this._trackingBlock = null;
    this._trackingBlockTimestamp = null;
    this._currentBlock = null;
    this._isRunning = false;
    // bind methods for cleaner syntax later
    this._performSync = this._performSync.bind(this);
    this._handleNewBlockNotification = this._handleNewBlockNotification.bind(this);
  }

  getTrackingBlock() {
    return this._trackingBlock;
  }

  getCurrentBlock() {
    return this._currentBlock;
  }

  awaitCurrentBlock() {
    var _this = this;

    return _asyncToGenerator(function* () {
      // return if available
      if (_this._currentBlock) return _this._currentBlock;
      // wait for "sync" event
      yield new Promise(function (resolve) {
        return _this.once('latest', resolve);
      });
      // return newly set current block
      return _this._currentBlock;
    })();
  }

  start(opts = {}) {
    var _this2 = this;

    return _asyncToGenerator(function* () {
      // abort if already started
      if (_this2._isRunning) return;
      _this2._isRunning = true;
      // if this._currentBlock
      if (opts.fromBlock) {
        // use specified start point
        yield _this2._setTrackingBlock((yield _this2._fetchBlockByNumber(opts.fromBlock)));
      } else {
        // or query for latest
        yield _this2._setTrackingBlock((yield _this2._fetchLatestBlock()));
      }
      if (_this2._provider.on) {
        yield _this2._initSubscription();
      } else {
        _this2._performSync().catch(function (err) {
          if (err) console.error(err);
        });
      }
    })();
  }

  stop() {
    var _this3 = this;

    return _asyncToGenerator(function* () {
      _this3._isRunning = false;
      if (_this3._provider.on) {
        yield _this3._removeSubscription();
      }
    })();
  }

  //
  // private
  //

  _setTrackingBlock(newBlock) {
    var _this4 = this;

    return _asyncToGenerator(function* () {
      if (_this4._trackingBlock && _this4._trackingBlock.hash === newBlock.hash) return;
      // check for large timestamp lapse
      const previous = _this4._trackingBlockTimestamp;
      const now = Date.now();
      // check for desynchronization (computer sleep or no internet)
      if (previous && now - previous > _this4._syncingTimeout) {
        _this4._trackingBlockTimestamp = null;
        yield _this4._warpToLatest();
      } else {
        _this4._trackingBlock = newBlock;
        _this4._trackingBlockTimestamp = now;
        _this4.emit('block', newBlock);
      }
    })();
  }

  _setCurrentBlock(newBlock) {
    var _this5 = this;

    return _asyncToGenerator(function* () {
      if (_this5._currentBlock && _this5._currentBlock.hash === newBlock.hash) return;
      const oldBlock = _this5._currentBlock;
      _this5._currentBlock = newBlock;
      _this5.emit('latest', newBlock);
      _this5.emit('sync', { newBlock, oldBlock });
    })();
  }

  _warpToLatest() {
    var _this6 = this;

    return _asyncToGenerator(function* () {
      // set latest as tracking block
      yield _this6._setTrackingBlock((yield _this6._fetchLatestBlock()));
    })();
  }

  _pollForNextBlock() {
    var _this7 = this;

    return _asyncToGenerator(function* () {
      setTimeout(function () {
        return _this7._performSync();
      }, _this7._pollingInterval);
    })();
  }

  _performSync() {
    var _this8 = this;

    return _asyncToGenerator(function* () {
      if (!_this8._isRunning) return;
      const trackingBlock = _this8.getTrackingBlock();
      if (!trackingBlock) throw new Error('RpcBlockTracker - tracking block is missing');
      const nextNumber = incrementHexNumber(trackingBlock.number);
      try {

        const newBlock = yield _this8._fetchBlockByNumber(nextNumber);
        if (newBlock) {
          // set as new tracking block
          yield _this8._setTrackingBlock(newBlock);
          // ask for next block
          _this8._performSync();
        } else {
          // set tracking block as current block
          yield _this8._setCurrentBlock(trackingBlock);
          // setup poll for next block
          _this8._pollForNextBlock();
        }
      } catch (err) {

        // hotfix for https://github.com/ethereumjs/testrpc/issues/290
        if (err.message.includes('index out of range') || err.message.includes("Couldn't find block by reference")) {
          // set tracking block as current block
          yield _this8._setCurrentBlock(trackingBlock);
          // setup poll for next block
          _this8._pollForNextBlock();
        } else {
          console.error(err);
          _this8._pollForNextBlock();
        }
      }
    })();
  }

  _handleNewBlockNotification(err, notification) {
    var _this9 = this;

    return _asyncToGenerator(function* () {
      if (notification.id != _this9._subscriptionId) return; // this notification isn't for us

      if (err) {
        _this9.emit('error', err);
        yield _this9._removeSubscription();
      }

      yield _this9._setTrackingBlock((yield _this9._fetchBlockByNumber(notification.result.number)));
    })();
  }

  _initSubscription() {
    var _this10 = this;

    return _asyncToGenerator(function* () {
      _this10._provider.on('data', _this10._handleNewBlockNotification);

      let result = yield pify(_this10._provider.sendAsync || _this10._provider.send)({
        jsonrpc: '2.0',
        id: new Date().getTime(),
        method: 'eth_subscribe',
        params: ['newHeads']
      });

      _this10._subscriptionId = result.result;
    })();
  }

  _removeSubscription() {
    var _this11 = this;

    return _asyncToGenerator(function* () {
      if (!_this11._subscriptionId) throw new Error("Not subscribed.");

      _this11._provider.removeListener('data', _this11._handleNewBlockNotification);

      yield pify(_this11._provider.sendAsync || _this11._provider.send)({
        jsonrpc: '2.0',
        id: new Date().getTime(),
        method: 'eth_unsubscribe',
        params: [_this11._subscriptionId]
      });

      delete _this11._subscriptionId;
    })();
  }

  _fetchLatestBlock() {
    return pify(this._query.getBlockByNumber).call(this._query, 'latest', true);
  }

  _fetchBlockByNumber(hexNumber) {
    const cleanHex = hexUtils.formatHex(hexNumber);
    return pify(this._query.getBlockByNumber).call(this._query, cleanHex, true);
  }

}

module.exports = RpcBlockTracker;

// ├─ difficulty: 0x2892ddca
// ├─ extraData: 0xd983010507846765746887676f312e372e348777696e646f7773
// ├─ gasLimit: 0x47e7c4
// ├─ gasUsed: 0x6384
// ├─ hash: 0xf60903687b1559b9c80f2d935b4c4f468ad95c3076928c432ec34f2ef3d4eec9
// ├─ logsBloom: 0x00000000000000000000000000000000000000000000000000000000000020000000000000000000000000040000000000000000000000000000000000000000000000000000000000000001000000000000000000000000000000000000000000000200000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000040000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000100000000000000000000000000000000000000000000000000000000000000000000000000000
// ├─ miner: 0x01711853335f857442ef6f349b2467c531731318
// ├─ mixHash: 0xf0d9bec999600eec92e8e4da8fc1182e357468c9ed2f849aa17e0e900412b352
// ├─ nonce: 0xd556d5a5504198e4
// ├─ number: 0x72ac8
// ├─ parentHash: 0xf5239c3ce1085194521435a5052494c02bbb1002b019684dcf368490ea6208e5
// ├─ receiptsRoot: 0x78c6f8236094b392bcc43b47b0dc1ce93ecd2875bfb5e4e4c3431e5af698ff99
// ├─ sha3Uncles: 0x1dcc4de8dec75d7aab85b567b6ccd41ad312451b948a7413f0a142fd40d49347
// ├─ size: 0x2ad
// ├─ stateRoot: 0x0554f145c481df2fa02ecd2da17071672740c3aa948c896f1465e6772f741ac6
// ├─ timestamp: 0x58955844
// ├─ totalDifficulty: 0x751d0dfa03c1
// ├─ transactions
// │  └─ 0
// │     ├─ blockHash: 0xf60903687b1559b9c80f2d935b4c4f468ad95c3076928c432ec34f2ef3d4eec9
// │     ├─ blockNumber: 0x72ac8
// │     ├─ from: 0x201354729f8d0f8b64e9a0c353c672c6a66b3857
// │     ├─ gas: 0x15f90
// │     ├─ gasPrice: 0x4a817c800
// │     ├─ hash: 0xd5a15d7c2449150db4f74f42a6ca0702150a24c46c5b406a7e1b3e44908ef44d
// │     ├─ input: 0xe1fa8e849bc10d87fb03c6b0603b05a3e29043c7e0b7c927119576a4bec457e96c7d7cde
// │     ├─ nonce: 0x323e
// │     ├─ to: 0xd10e3be2bc8f959bc8c41cf65f60de721cf89adf
// │     ├─ transactionIndex: 0x0
// │     ├─ value: 0x0
// │     ├─ v: 0x29
// │     ├─ r: 0xf35f8ab241e6bb3ccaffd21b268dbfc7fcb5df1c1fb83ee5306207e4a1a3e954
// │     └─ s: 0x1610cdac2782c91065fd43584cd8974f7f3b4e6d46a2aafe7b101788285bf3f2
// ├─ transactionsRoot: 0xb090c32d840dec1e9752719f21bbae4a73e58333aecb89bc3b8ed559fb2712a3
// └─ uncles